import cn from 'classnames';
import React from 'react';
import {Control, DefaultValues, FieldValues, Path, useForm, UseFormHandleSubmit} from 'react-hook-form';

import type {LoginErrorDetails} from '../LoginPageV2.types';

const DEFAULT_INPUT_CLASSNAME = 'ica-auth-input';

interface AuthFormField {
    name: string;
    className?: string;
    pattern?: RegExp;
}

interface FieldState {
    value: string;
    isEntered: boolean;
    isValid: boolean;
    className: string;
}

interface UseAuthFormParams<T extends FieldValues> {
    fields: AuthFormField[];
    onSubmit: (data: T) => void;
    setError?: (error: LoginErrorDetails) => void;
    defaultValues?: DefaultValues<T>;
    inputClassName?: string;
}

interface UseAuthFormReturn<T extends FieldValues> {
    control: Control<T>;
    handleFormSubmit: ReturnType<UseFormHandleSubmit<T>>;
    handleFormKeyDown: (event: React.KeyboardEvent<HTMLFormElement>) => Promise<void>;
    fieldStates: Record<string, FieldState>;
    clearField: (fieldName: Path<T>) => void;
    hasSubmitted: boolean;
}

const useAuthForm = <T extends FieldValues>({
    fields = [],
    onSubmit,
    setError,
    defaultValues = {} as DefaultValues<T>,
    inputClassName,
}: UseAuthFormParams<T>): UseAuthFormReturn<T> => {
    const {control, handleSubmit, watch, setValue, trigger, formState} = useForm<T>({
        defaultValues,
        // validate fields when they lose focus, then re-validate them on change
        mode: 'onBlur',
        reValidateMode: 'onChange',
    });

    const hasSubmitted = formState.submitCount > 0;

    const fieldStates = fields.reduce(
        (acc, field) => {
            const fieldName = field.name as Path<T>;
            const value = watch(fieldName) as unknown as string;
            // @ts-ignore touchedFields is keyed by field name at runtime
            const isTouched = !!formState.touchedFields[fieldName];
            const hasValue = !!value;
            const isPatternValid = field.pattern && value ? field.pattern.test(value) : true;
            // field is considered "interacted with" once it has been blurred at least once
            const shouldValidate = hasSubmitted || isTouched;
            const isEntered = shouldValidate ? hasValue : true;
            const isValid = shouldValidate ? hasValue && isPatternValid : true;

            acc[field.name] = {
                value,
                isEntered,
                isValid,
                className: cn(
                    'text-start h-13 w-full border focus:border border-primary-dark-25 focus:border-primary pr-10 rtl:pl-10 rtl:pr-3',
                    inputClassName,
                    DEFAULT_INPUT_CLASSNAME,
                    field.className,
                    (!isValid || !isEntered) && 'border-error border-2 focus:border-error focus:border-2'
                ),
            };

            return acc;
        },
        {} as Record<string, FieldState>
    );

    const clearField = (fieldName: Path<T>): void => {
        setValue(fieldName, '' as any);
    };

    const onFormSubmit = (data: T): void => {
        onSubmit(data);
        (document.activeElement as HTMLElement)?.blur();
    };

    const handleFormSubmit = handleSubmit(onFormSubmit);

    const handleFormKeyDown = async (event: React.KeyboardEvent<HTMLFormElement>): Promise<void> => {
        if (event.key !== 'Enter') {
            return;
        }

        const inputs = Array.from(document.querySelectorAll(`.${DEFAULT_INPUT_CLASSNAME}`)) as HTMLElement[];
        const currentIndex = inputs.indexOf(event.target as HTMLElement);

        if (currentIndex === -1) {
            return;
        }

        event.preventDefault();

        const currentField = fields[currentIndex];

        if (!currentField) {
            return;
        }

        const isCurrentFieldValid = await trigger(currentField.name as Path<T>);

        if (isCurrentFieldValid && currentIndex < inputs.length - 1) {
            inputs[currentIndex + 1].focus();
            return;
        }

        if (currentIndex === inputs.length - 1) {
            handleFormSubmit();
        }
    };

    return {
        control,
        handleFormSubmit,
        handleFormKeyDown,
        fieldStates,
        clearField,
        hasSubmitted,
    };
};

export default useAuthForm;
